import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import { DocumentDetailsSection, FileUploadSection, FileAccessSection } from "../components/DocumentComponents";
import axiosInstance from "../../services/AxiosInstance";
import { createStaffDocument, updateStaffDocument, getStaffById } from "../../services/StaffService";
import { canUploadDocuments, hasDocumentSubscription } from "../../utils/documentAccess";

const StaffDocumentForm = () => {
  const navigate = useNavigate();
  const { staffId, documentId } = useParams();
  const isEditMode = !!documentId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);
  const [classifications, setClassifications] = useState([]);
  const [files, setFiles] = useState([]);
  const [existingFileInfo, setExistingFileInfo] = useState(null);
  
  // File access state
  const [isPrivate, setIsPrivate] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [hasDocumentModuleSubscription, setHasDocumentModuleSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  const [formData, setFormData] = useState({
    remarks: "",
    verification_status: "Pending",
    document_title: "",
    issued_by: "",
    issued_date: "",
    expiry_date: "",
    storage_path: "",
    is_linked_document: false,
    document_id: null,
    classification: "",
    file: null
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    checkDocumentSubscription();
    fetchDocumentClassifications();
    fetchBranches();
    fetchDepartments();
    fetchSubDepartments();
    fetchUsers();
    if (isEditMode) {
      fetchDocumentData();
    }
  }, [staffId, documentId]);

  // Check document module subscription
  const checkDocumentSubscription = async () => {
    try {
      setCheckingSubscription(true);
      const hasSubscription = await hasDocumentSubscription();
      setHasDocumentModuleSubscription(hasSubscription);
      
      // If no subscription and is_linked_document is checked, uncheck it
      if (!hasSubscription && formData.is_linked_document) {
        setFormData((prev) => ({
          ...prev,
          is_linked_document: false,
        }));
      }
    } catch (error) {
      console.error('Error checking document subscription:', error);
      setHasDocumentModuleSubscription(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Filter departments and sub-departments when branches are selected and data is loaded
  useEffect(() => {
    if (selectedBranches.length > 0 && departments.length > 0) {
      const filtered = departments.filter(dept => selectedBranches.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      if (subDepartments.length > 0) {
        const validDeptIds = filtered.map(d => d.id);
        const filteredSubDepts = subDepartments.filter(sd => {
          const sdDeptId = sd.department_id || sd.departmentId;
          return validDeptIds.includes(sdDeptId);
        });
        setFilteredSubDepartments(filteredSubDepts);
      }
    } else if (departments.length > 0) {
      setFilteredDepartments(departments);
      if (subDepartments.length > 0) {
        setFilteredSubDepartments(subDepartments);
      }
    }
  }, [selectedBranches, departments, subDepartments]);

  const fetchDocumentClassifications = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const response = await axiosInstance.get(`/document-categories?corporate_id=${corporateId}`);
      const data = response.data;
      
      let classificationsList = [];
      if (data && data.success === true && Array.isArray(data.data)) {
        classificationsList = data.data;
      } else if (data && Array.isArray(data.data)) {
        classificationsList = data.data;
      } else if (Array.isArray(data)) {
        classificationsList = data;
      }
      
      setClassifications(classificationsList);
    } catch (error) {
      console.error('Error fetching document classifications:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        const deptList = data.data || data || [];
        setDepartments(deptList);
        setFilteredDepartments(deptList);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;
      if (res) {
        const subDeptList = data.data || data || [];
        setSubDepartments(subDeptList);
        setFilteredSubDepartments(subDeptList);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const params = new URLSearchParams();
      params.append('corporateId', corporateId);
      
      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle branch change - filter departments
  const handleBranchChange = (branchIds, selectedOptions) => {
    setSelectedBranches(branchIds);
    // Filter departments based on selected branches
    if (branchIds.length > 0) {
      const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      // Also filter sub-departments
      const validDeptIds = filtered.map(d => d.id);
      const filteredSubDepts = subDepartments.filter(sd => {
        const sdDeptId = sd.department_id || sd.departmentId;
        return validDeptIds.includes(sdDeptId);
      });
      setFilteredSubDepartments(filteredSubDepts);
    } else {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle department change - filter sub-departments
  const handleDepartmentChange = (departmentIds, selectedOptions) => {
    setSelectedDepartments(departmentIds);
    if (departmentIds.length > 0) {
      const filtered = subDepartments.filter(sd => {
        const sdDeptId = sd.department_id || sd.departmentId;
        return departmentIds.includes(sdDeptId);
      });
      setFilteredSubDepartments(filtered);
    } else {
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle sub-department change
  const handleSubDepartmentChange = (subDepartmentIds, selectedOptions) => {
    setSelectedSubDepartments(subDepartmentIds);
  };

  // Handle user change
  const handleUserChange = (userIds, selectedOptions) => {
    setSelectedUsers(userIds);
  };

  // Handle existing file download
  const handleDownloadExistingFile = async () => {
    if (!existingFileInfo?.fileUrl) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(existingFileInfo.fileUrl, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/octet-stream'
        }
      });

      if (response.status === 200) {
        const fileName = existingFileInfo.fileName || 'document';
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        notifySuccess('File downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      notifyError(error?.response?.data?.message || 'Failed to download file');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(staffId);
      const staff = res.data.data || res.data;
      const document = staff.StaffDocuments?.find(doc => doc.id === parseInt(documentId));
      
      if (document) {
        // Get classification from document.classification or Document.category_id
        // Convert to number since classification options use category.id (number) as value
        const classificationValue = document.classification 
          ? (typeof document.classification === 'string' ? parseInt(document.classification, 10) : document.classification)
          : (document.Document?.category_id || null);
        
        setFormData({
          remarks: document.remarks || "",
          verification_status: document.verification_status || "Pending",
          document_title: document.document_title || "",
          issued_by: document.issued_by || "",
          issued_date: document.issued_date ? new Date(document.issued_date).toISOString().split('T')[0] : "",
          expiry_date: document.expiry_date ? new Date(document.expiry_date).toISOString().split('T')[0] : "",
          storage_path: document.storage_path || "",
          is_linked_document: document.is_linked_document || false,
          document_id: document.document_id || null,
          classification: classificationValue,
          file: null
        });

        // Set existing file info for display
        if (document.storage_path) {
          const fileName = document.Document?.file_name || document.storage_path.split(/[/\\]/).pop();
          const fileSize = document.Document?.file_size || null;
          // Create download URL based on document type
          let fileUrl = null;
          if (document.is_linked_document && document.document_id) {
            fileUrl = `/documents/${document.document_id}/download`;
          } else {
            fileUrl = `/staff/${staffId}/documents/${document.id}/download`;
          }
          
          setExistingFileInfo({
            fileName,
            fileSize,
            fileUrl
          });
        }

        // Pre-fill file access fields if document is linked
        if (document.is_linked_document && document.Document) {
          setIsPrivate(document.Document.is_private || false);
          
          // Extract IDs from arrays
          const branchIds = document.Document.Branches?.map(b => b.id) || [];
          const departmentIds = document.Document.Departments?.map(d => d.id) || [];
          const subDepartmentIds = document.Document.SubDepartments?.map(sd => sd.id) || [];
          const userIds = document.Document.Users?.map(u => u.id || u.CorporateUserId) || [];
          
          setSelectedBranches(branchIds);
          setSelectedDepartments(departmentIds);
          setSelectedSubDepartments(subDepartmentIds);
          setSelectedUsers(userIds);
        }
      } else {
        notifyError("Document not found");
        navigate(`/staff/staffDetails/${staffId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Document fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load document data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Prevent checking "Link to Documents" if no subscription
    if (type === 'checkbox' && name === 'is_linked_document' && checked && !hasDocumentModuleSubscription) {
      notifyError('Document module subscription is required to link documents');
      return;
    }
    
    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle file changes from FileUploadSection
  const handleFilesChange = (newFiles) => {
    setFiles(newFiles);
    // Update formData.file to maintain compatibility
    setFormData((prev) => ({
      ...prev,
      file: newFiles.length > 0 ? newFiles[0] : null,
    }));
    // Clear file error if file is selected
    if (newFiles.length > 0 && errors.file) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.file;
        return newErrors;
      });
    }
  };

  const isOnlySpecialChars = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return false;
    return !/[a-zA-Z0-9]/.test(trimmed);
  };

  const validate = () => {
    const errs = {};
    // document_title is required for linked documents
    if (formData.is_linked_document && !formData.document_title.trim()) {
      errs.document_title = "Document title is required for linked documents";
    } else if (formData.document_title.trim() && isOnlySpecialChars(formData.document_title)) {
      errs.document_title = "Document name cannot contain only special characters.";
    }
    // File is required for new documents
    if (!isEditMode && files.length === 0) {
      errs.file = "Please upload a document file";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      // For new documents, file is required
      if (!isEditMode && !formData.file) {
        notifyError("Please upload a document file");
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      // Check storage space before upload (only for new files, not in edit mode or if no new file)
      if (formData.file && (!isEditMode || (isEditMode && formData.file))) {
        const fileSize = formData.file.size || 0;
        const accessCheck = await canUploadDocuments(fileSize);
        
        if (!accessCheck.canUpload) {
          if (accessCheck.reason === 'insufficient_space') {
            notifyError(accessCheck.message || 'Insufficient storage space to upload this file');
          } else {
            notifyError(accessCheck.message || 'You do not have permission to upload documents');
          }
          setIsSubmitting(false);
          setLoading(false);
          return;
        }
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append file if present (required for new documents, optional for edits)
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      // Append all other fields
      if (formData.remarks) {
        formDataToSend.append('remarks', formData.remarks);
      }
      
      formDataToSend.append('verification_status', formData.verification_status || 'Pending');
      
      if (formData.document_title) {
        formDataToSend.append('document_title', formData.document_title);
      }
      
      if (formData.issued_by) {
        formDataToSend.append('issued_by', formData.issued_by);
      }
      
      if (formData.issued_date) {
        formDataToSend.append('issued_date', formData.issued_date);
      }
      
      if (formData.expiry_date) {
        formDataToSend.append('expiry_date', formData.expiry_date);
      }
      
      formDataToSend.append('is_linked_document', formData.is_linked_document ? 'true' : 'false');
      
      if (formData.classification) {
        formDataToSend.append('classification', formData.classification);
      }

      // File access fields - only send when linking to document module
      if (formData.is_linked_document) {
        formDataToSend.append('is_private', isPrivate ? 'true' : 'false');
        
        if (selectedBranches.length > 0) {
          formDataToSend.append('branch_ids', JSON.stringify(selectedBranches));
        }
        
        if (selectedDepartments.length > 0) {
          formDataToSend.append('department_ids', JSON.stringify(selectedDepartments));
        }
        
        if (selectedSubDepartments.length > 0) {
          formDataToSend.append('sub_department_ids', JSON.stringify(selectedSubDepartments));
        }
        
        if (selectedUsers.length > 0) {
          formDataToSend.append('user_ids', JSON.stringify(selectedUsers));
        }
      }

      if (isEditMode) {
        // For updates, we can send as JSON or FormData depending on whether file is present
        if (formData.file) {
          await updateStaffDocument(staffId, documentId, formDataToSend);
        } else {
          // If no file, send as JSON object
          const payload = { ...formData };
          delete payload.file;
          // Add file access fields to JSON payload - only when linking to document module
          if (formData.is_linked_document) {
            payload.is_private = isPrivate;
            if (selectedBranches.length > 0) {
              payload.branch_ids = JSON.stringify(selectedBranches);
            }
            if (selectedDepartments.length > 0) {
              payload.department_ids = JSON.stringify(selectedDepartments);
            }
            if (selectedSubDepartments.length > 0) {
              payload.sub_department_ids = JSON.stringify(selectedSubDepartments);
            }
            if (selectedUsers.length > 0) {
              payload.user_ids = JSON.stringify(selectedUsers);
            }
          }
          await updateStaffDocument(staffId, documentId, payload);
        }
        notifySuccess("Document updated successfully");
      } else {
        // For new documents, always use FormData
        await createStaffDocument(staffId, formDataToSend);
        notifySuccess("Document added successfully");
      }
      setTimeout(() => navigate(`/staff/staffDetails/${staffId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} document`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Document" : "Add Document"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="is_linked_document"
                  id="is_linked_document"
                  checked={formData.is_linked_document}
                  onChange={handleChange}
                  disabled={!hasDocumentModuleSubscription || checkingSubscription}
                />
                <label className="form-check-label" htmlFor="is_linked_document" style={{ 
                  opacity: (!hasDocumentModuleSubscription || checkingSubscription) ? 0.6 : 1,
                  cursor: (!hasDocumentModuleSubscription || checkingSubscription) ? 'not-allowed' : 'pointer'
                }}>
                  Link to Documents
                </label>
                {!hasDocumentModuleSubscription && !checkingSubscription && (
                  <small className="text-muted d-block mt-1">
                    <i className="fas fa-info-circle me-1"></i>
                    Document module subscription is required to link documents
                  </small>
                )}
              </div>
            </div>

            {/* Show file upload section - always show, but hide upload for linked documents with document_id */}
            <div className="col-12 mb-3">
              {errors.file && (
                <div className="alert alert-danger mb-2">
                  <small>{errors.file}</small>
                </div>
              )}
              <FileUploadSection
                files={files}
                onFilesChange={handleFilesChange}
                hideFileUpload={isEditMode && formData.is_linked_document && formData.document_id}
                existingFileName={existingFileInfo?.fileName}
                existingFileSize={existingFileInfo?.fileSize}
                existingFileUrl={existingFileInfo?.fileUrl}
                onDownloadExistingFile={handleDownloadExistingFile}
              />
            </div>

            {/* Use DocumentDetailsSection for document fields */}
            <div className="col-md-12">
              <DocumentDetailsSection
                selectedCategory={formData.classification || null}
                onCategoryChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    classification: value || "",
                  }));
                }}
                categories={classifications}
                documentTitle={formData.document_title}
                onDocumentTitleChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    document_title: value,
                  }));
                  if (errors.document_title) setErrors((prev) => { const n = { ...prev }; delete n.document_title; return n; });
                }}
                documentTitleError={errors.document_title}
                documentIssuingAuthority={formData.issued_by}
                onDocumentIssuingAuthorityChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    issued_by: value,
                  }));
                }}
                documentIssueDate={formData.issued_date}
                onDocumentIssueDateChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    issued_date: value,
                  }));
                }}
                documentExpiryDate={formData.expiry_date}
                onDocumentExpiryDateChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    expiry_date: value,
                  }));
                }}
                otherComments={formData.remarks}
                onOtherCommentsChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    remarks: value,
                  }));
                }}
                showClassification={formData.is_linked_document || (isEditMode && formData.classification)}
                showRegisteredTo={false}
                showReferenceNumber={false}
                showRemindMeBefore={false}
                disabled={formData.is_linked_document && !hasDocumentModuleSubscription}
              />
            </div>

            {/* Verification Status - moved to bottom */}
            <div className="col-12 col-md-6 mb-3">
              <label>Verification Status*</label>
              <select
                name="verification_status"
                value={formData.verification_status}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            {/* File Access Section - only show when linking to document module */}
            {formData.is_linked_document && (
              <div className="col-md-12">
                {!hasDocumentModuleSubscription ? (
                  <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Document module subscription is required to use file access settings. Please subscribe to the document module to enable these features.
                  </div>
                ) : (
                  <FileAccessSection
                    isPrivate={isPrivate}
                    onIsPrivateChange={setIsPrivate}
                    selectedBranches={selectedBranches}
                    onBranchesChange={handleBranchChange}
                    selectedDepartments={selectedDepartments}
                    onDepartmentsChange={handleDepartmentChange}
                    selectedSubDepartments={selectedSubDepartments}
                    onSubDepartmentsChange={handleSubDepartmentChange}
                    branches={branches}
                    departments={departments}
                    subDepartments={subDepartments}
                    filteredDepartments={filteredDepartments}
                    filteredSubDepartments={filteredSubDepartments}
                    availableUsers={availableUsers}
                    selectedUsers={selectedUsers}
                    onUsersChange={handleUserChange}
                  />
                )}
              </div>
            )}
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
                disabled={isSubmitting}
                style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default StaffDocumentForm;
